import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import {
	AppBar,
	Box,
	Container,
	CssBaseline,
	Grid,
	Link,
	List,
	ListItem,
	ListItemIcon,
	styled,
	Toolbar,
	Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { FC, PropsWithChildren } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';

import GoLogo from '../../../../../assets/img/go-combo-mark.svg';
import AccountSyncBackground from '../../../../../assets/img/landing-page/account-sync.png';
import ReceiptBackground from '../../../../../assets/img/landing-page/receipt.png';
import ReconciliationBackground from '../../../../../assets/img/landing-page/reconciliation.png';
import ShieldLogo from '../../../../../assets/img/landing-page/shield.svg';
import StatementReportBackground from '../../../../../assets/img/landing-page/statement-rep.png';
import { UI_BUTTON_PRIMARY, UI_BUTTON_SECONDARY } from '../../../constants/uiConstants';
import SimpleButton from '../../atoms/button/simpleButton';

const StyledAppBar = styled(AppBar)(({ theme }: { theme: Theme }) => ({
	backgroundColor: theme.palette.tsGoColor.white,
	boxShadow: 'none',
}));

const StyledToolBar = styled(Toolbar)(() => ({
	display: 'flex',
	justifyContent: 'space-between',
	padding: '10px',
}));

const StyledIntroBox = styled(Box)(({ theme }: { theme: Theme }) => ({
	backgroundColor: theme.palette.tsGoColor.white,
	display: 'flex',
	flexDirection: 'column',
	margin: '0 auto',
	padding: '20px 20px 40px',
	maxWidth: '1000px',
	[theme.breakpoints.down(415)]: {
		maxWidth: '90%',
		padding: '20px 10px 40px',
		margin: '40px 0',
	},
}));

const StyledHowItWorksBox = styled(Box)(() => ({
	backgroundColor: 'hsl(200, 21%, 92%)',
	color: 'hsl(198, 20%, 59%)',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	padding: '10px',
	textAlign: 'center',
	textWrap: 'balance',
}));

const StyledAccountingSection = styled(Box)(({ theme }: { theme: Theme }) => ({
	color: 'hsl(198, 20%, 59%)',
	display: 'flex',
	flexDirection: 'column',
	margin: '0 auto',
	maxWidth: '1000px',
	padding: '20px',
	[theme.breakpoints.down(415)]: {
		margin: 0,
		textWrap: 'balance',
	},
}));

const StyledBankGradeSecuritySection = styled(Box)(() => ({
	backgroundColor: 'hsl(198, 20%, 49%)',
	color: 'hsl(0, 0%, 100%)',
	display: 'flex',
	flexDirection: 'column',
	margin: '0 auto',
	maxWidth: '1000px',
}));

const TakeControlSection = styled(Box)(({ theme }: { theme: Theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	padding: '80px 10px',
	width: 'auto',
	textAlign: 'center',
	[theme.breakpoints.down(415)]: {
		width: '80%',
	},
}));

const FooterSection = styled(Box)(() => ({
	backgroundColor: 'hsl(198, 20%, 83%)',
}));

const LandingPage: FC<PropsWithChildren> = () => {
	const navigate = useNavigate();

	return (
		<>
			<Box sx={{ bgcolor: 'white', display: 'block', overflowY: 'auto', textWrap: 'pretty' }}>
				<CssBaseline />
				<StyledAppBar>
					<StyledToolBar>
						<img src={GoLogo as any} alt="Tradeshift GO" width={200} />
						<SimpleButton
							dataTestId="navigateToLogin-btn"
							label="Login"
							color={UI_BUTTON_SECONDARY}
							onClick={() => navigate('/signin/login')}
						/>
					</StyledToolBar>
				</StyledAppBar>
				<StyledIntroBox sx={{ pt: 5, boxSizing: 'border-box' }}>
					<Typography ts-variant="intro-header">Share and control access to your corporate card</Typography>
					<Grid container spacing={2} sx={{ alignItems: 'center', display: 'flex' }}>
						<Grid
							sx={{ display: 'flex', flexDirection: 'column' }}
							size={{
								xs: 12,
								sm: 12,
								md: 5,
								lg: 6,
							}}
						>
							<Typography ts-variant="pitch-text">
								Let employees request access to your corporate card without exposing your card details, all while
								enforcing budgets and tracking every purchase.
							</Typography>
							<SimpleButton
								dataTestId="requestDemo-btn"
								label="Get Started"
								color={UI_BUTTON_PRIMARY}
								onClick={() => {
									void navigate('/request-demo');
								}}
								style={{ margin: 'auto' }}
							/>
						</Grid>
						<Grid
							sx={{ height: '315px', position: 'relative', maxWidth: '515px' }}
							size={{
								xs: 12,
								sm: 12,
								md: 7,
								lg: 6,
							}}
						>
							<iframe
								id="demo-video"
								src="https://www.youtube.com/embed/C6Fs5BAGLdE?si=nPFX0riE6cvttVEx"
								title="Tradeshift Go Demo Video"
								frameBorder="0"
								allowFullScreen
								width="100%"
								height="100%"
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
								referrerPolicy="strict-origin-when-cross-origin"
							></iframe>
						</Grid>
					</Grid>
				</StyledIntroBox>
				<StyledHowItWorksBox>
					<Typography variant="h4" ts-variant="how-it-works-text">
						How It Works
					</Typography>
					<Typography ts-variant="how-it-works-info">
						It takes <strong>1 minute</strong> to enroll your corporate card with Go, to grant your employees simple &
						secure card access
					</Typography>
					<Container ts-variant="items">
						<Container ts-variant="item" ts-item-variant="default">
							<Typography
								variant="h4"
								sx={{
									fontWeight: 300,
									mb: 1,
								}}
							>
								1. Request funds
							</Typography>
							<Typography>
								Employee requests funds from the company card to make the purchase. Requested funds can have set spend
								limitations.
							</Typography>
							<List>
								<ListItem disablePadding>
									<ListItemIcon>
										<CheckCircleIcon color="success" />
									</ListItemIcon>
									<Typography ts-variant="how-it-works-steps">For single or multiple purchases</Typography>
								</ListItem>
								<ListItem disablePadding>
									<ListItemIcon>
										<CheckCircleIcon color="success" />
									</ListItemIcon>
									<Typography ts-variant="how-it-works-steps">Set an expiration date</Typography>
								</ListItem>
								<ListItem disablePadding>
									<ListItemIcon>
										<CheckCircleIcon color="success" />
									</ListItemIcon>
									<Typography ts-variant="how-it-works-steps">Refresh funds every week, month, year</Typography>
								</ListItem>
								<ListItem disablePadding>
									<ListItemIcon>
										<CheckCircleIcon color="success" />
									</ListItemIcon>
									<Typography ts-variant="how-it-works-steps">Limit to certain expense categories</Typography>
								</ListItem>
							</List>
						</Container>
						<Container ts-variant="item" ts-item-variant="purchase-request-form" />
						<Container ts-variant="item" ts-item-variant="mobile">
							<Typography
								variant="h4"
								sx={{
									fontWeight: 300,
								}}
							>
								2. Review and approve
							</Typography>
							<Typography>
								Manager receives each request for review and approval. Spend limitations can also be adjusted.
							</Typography>
						</Container>
						<Container ts-variant="item" ts-item-variant="approval-item" />
						<Container ts-variant="item" ts-item-variant="review-n-approve">
							<Typography
								variant="h4"
								sx={{
									fontWeight: 300,
								}}
							>
								2. Review and approve
							</Typography>
							<Typography>
								Manager receives each request for review and approval. Spend limitations can also be adjusted.
							</Typography>
						</Container>
						<Container ts-variant="item" ts-item-variant="default">
							<Typography
								variant="h4"
								sx={{
									fontWeight: 300,
									mb: 1,
								}}
							>
								3. Pay with virtual card
							</Typography>
							<List>
								<ListItem disablePadding>
									<ListItemIcon>
										<CheckCircleIcon color="success" />
									</ListItemIcon>
									<Typography ts-variant="how-it-works-steps">Preset with requested spend limitations</Typography>
								</ListItem>
								<ListItem disablePadding>
									<ListItemIcon>
										<CheckCircleIcon color="success" />
									</ListItemIcon>
									<Typography ts-variant="how-it-works-steps">
										Distinct number and CVC hides real card details
									</Typography>
								</ListItem>
								<ListItem disablePadding>
									<ListItemIcon>
										<CheckCircleIcon color="success" />
									</ListItemIcon>
									<Typography ts-variant="how-it-works-steps">Cancel with a click if compromised</Typography>
								</ListItem>
							</List>
						</Container>
						<Container ts-variant="item" ts-item-variant="virtual-card" />
					</Container>
				</StyledHowItWorksBox>
				<Box sx={{ backgroundColor: 'hsl(204, 22%, 95%)' }}>
					<StyledAccountingSection>
						<Typography variant="h4" ts-variant="speed-up-accounting-text">
							Speed up your accounting
						</Typography>
						<Container ts-variant="accounting-benefits">
							<Grid
								container
								sx={{
									textAlign: 'left',
								}}
							>
								<Grid
									sx={{ padding: '20px 40px 20px 40px' }}
									size={{
										xs: 12,
										sm: 12,
										md: 6,
									}}
								>
									<img src={ReceiptBackground} alt="" />
									<Typography variant="h5">Automated receipt collection</Typography>
									<Typography>
										Go reminds employees of any missing receipts, which can be attached simply by responding to the
										notification, or in the case of an email receipt, forwarding it to Go.
									</Typography>
								</Grid>
								<Grid
									sx={{ padding: '20px 40px 20px 40px' }}
									size={{
										xs: 12,
										sm: 12,
										md: 6,
									}}
								>
									<img src={AccountSyncBackground} alt="" />
									<Typography variant="h5">Detailed statements and reporting</Typography>
									<Typography>
										Easily search historical transactions and explore trends in employee spend across any time period.
									</Typography>
								</Grid>
								<Grid
									sx={{ padding: '20px 40px 20px 40px' }}
									size={{
										xs: 12,
										sm: 12,
										md: 6,
									}}
								>
									<img src={ReconciliationBackground} alt="" />
									<Typography variant="h5">Stress-free reconciliation</Typography>
									<Typography>
										Automatically create accurate reconciliation reports which include information on purchasers,
										approvers, receipts, account codes and more.
									</Typography>
								</Grid>
								<Grid
									sx={{ padding: '20px 40px 20px 40px' }}
									size={{
										xs: 12,
										sm: 12,
										md: 6,
									}}
								>
									<img src={StatementReportBackground} alt="" />
									<Typography variant="h5">Syncs with your accounting solution</Typography>
									<Typography>
										Import your QuickBooks, NetSuite or Xero account setup directly into Go. Export the latest
										transactions into your accounting solution with a single click.
									</Typography>
								</Grid>
							</Grid>
						</Container>
					</StyledAccountingSection>
					<StyledBankGradeSecuritySection sx={{ textAlign: 'center' }}>
						<Grid
							container
							sx={{
								p: 1,
							}}
						>
							<Grid
								size={{
									xs: 10,
									sm: 12,
									md: 12,
									lg: 12,
								}}
							>
								<Typography
									variant="h5"
									sx={{
										m: 1,
									}}
								>
									Bank-grade security
								</Typography>
								<Box>
									<img src={ShieldLogo as any} alt="Shield" />
								</Box>
							</Grid>
						</Grid>
						<Container ts-variant="security-benefits">
							<Grid container>
								<Grid
									sx={{
										p: 3,
									}}
									size={{
										xs: 12,
										sm: 12,
										md: 6,
									}}
								>
									<Typography variant="h5">Fraud Protection</Typography>
									<Typography>
										Go works with your credit card network to guarantee the same fraud prevention standards you would
										expect when working with the most elite financial institutions.
									</Typography>
								</Grid>
								<Grid
									sx={{
										p: 3,
									}}
									size={{
										xs: 12,
										sm: 12,
										md: 6,
									}}
								>
									<Typography variant="h5">Data Encryption</Typography>
									<Typography>
										All your financial information is encrypted and on servers in secure locations.
									</Typography>
								</Grid>
							</Grid>
						</Container>
					</StyledBankGradeSecuritySection>
					<TakeControlSection>
						<Typography variant="h4" ts-variant="take-control-text">
							Are you ready to take control of your card spend?
						</Typography>
						<SimpleButton
							dataTestId="redirectToRequestDemo-btn"
							label="Get Started"
							color={UI_BUTTON_PRIMARY}
							onClick={() => {
								void navigate('/request-demo');
							}}
							style={{ margin: 'auto' }}
						/>
					</TakeControlSection>
				</Box>
				<FooterSection>
					<Container ts-variant="landing-footer">
						<Typography>Copyright ® 2025 Tradeshift Inc. All rights reserved.</Typography>
						<Box sx={{ color: 'hsl(198, 20%, 59%)', display: 'flex', justifyContent: 'flex-start' }}>
							<Link to="/tos" component={RouterLink} underline="hover" ts-variant="footer-links">
								Privacy Policy
							</Link>
							<Link to="/tos" component={RouterLink} underline="hover" ts-variant="footer-links">
								Terms of Service
							</Link>
							<TwitterIcon
								onClick={() => {
									globalThis.location.href = 'https://twitter.com/tradeshiftgo';
								}}
								sx={{ cursor: 'pointer' }}
							/>
							<FacebookIcon
								onClick={() => {
									globalThis.location.href = 'https://www.facebook.com/Tradeshift';
								}}
								sx={{ cursor: 'pointer' }}
							/>
							<Link
								onClick={() => navigate('https://medium.com/@tradeshiftgo')}
								underline="none"
								ts-variant="footer-links"
							/>
						</Box>
					</Container>
				</FooterSection>
			</Box>
		</>
	);
};
export default LandingPage;
